import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { FixedSizeList as List } from 'react-window';
import { logger } from '../utils/logger';

// Move months array outside component to prevent recreation on every render
const MONTHS = [
  { name: 'Jan', value: 0 },
  { name: 'Feb', value: 1 },
  { name: 'Mar', value: 2 },
  { name: 'Apr', value: 3 },
  { name: 'May', value: 4 },
  { name: 'Jun', value: 5 },
  { name: 'Jul', value: 6 },
  { name: 'Aug', value: 7 },
  { name: 'Sep', value: 8 },
  { name: 'Oct', value: 9 },
  { name: 'Nov', value: 10 },
  { name: 'Dec', value: 11 }
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [userRole, setUserRole] = useState('user');
  const [filterLabel, setFilterLabel] = useState('');
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [adHocCharges, setAdHocCharges] = useState([]);
  
  // New state variables for filters
  const [selectedTutor, setSelectedTutor] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [availableTutors, setAvailableTutors] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [allContractors, setAllContractors] = useState([]);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25); // Reduced to 25 for much better performance
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);

  // Loading state for table
  const [tableLoading, setTableLoading] = useState(false);
  
  // Summary totals state (for all appointments in date range, not just paginated ones)
  const [summaryTotals, setSummaryTotals] = useState({
    totalRevenue: 0,
    totalTutorPay: 0,
    totalProfit: 0,
    totalAppointments: 0,
    completedRevenue: 0,
    completedTutorPay: 0,
    completedProfit: 0,
    completedAppointments: 0,
    completedDurationMinutes: 0,
    statusCounts: {}
  });
  
  // Virtualization state
  const [useVirtualization, setUseVirtualization] = useState(false);

  // Cache for fetched data
  const dataCache = useRef(new Map());
  const lastFetchParams = useRef({});

  // Debounce filter changes
  const filterTimeoutRef = useRef(null);
  
  // Ref for download button
  const downloadButtonRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (!decoded || !decoded.username) {
        navigate('/login');
      } else {
        setUserRole(decoded.role || 'user');
      }
    } catch (error) {
      navigate('/login');
    }
  }, [navigate]);

  // Set current month as default (but don't auto-fetch)
  useEffect(() => {
    if (!startDate && !endDate) {
      const currentDate = new Date();
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    }
  }, [startDate, endDate]);

  // Debug effect to log when dates change (only in development)
  useEffect(() => {
    logger.debug('Dates changed:', { startDate, endDate });
  }, [startDate, endDate]);

  // Fetch all contractors for employment type filtering
  const fetchAllContractors = useCallback(async () => {
    try {
      const response = await fetch('/api/contractors/all', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllContractors(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  }, []);

  useEffect(() => {
    fetchAllContractors();
  }, [fetchAllContractors]);

  // Fetch all available labels for the date range (not just filtered appointments)
  const fetchAvailableLabels = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    try {
      const startUTC = new Date(`${startDate}T00:00:00-05:00`).toISOString();
      const endUTC = new Date(`${endDate}T23:59:59-05:00`).toISOString();
      
      const response = await fetch(`/api/appointments/labels?start=${startUTC}&end=${endUTC}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableLabels(data.labels || []);
      }
    } catch (error) {
      console.error('Error fetching available labels:', error);
    }
  }, [startDate, endDate]);

  // Fetch available labels when date range changes
  useEffect(() => {
    fetchAvailableLabels();
  }, [fetchAvailableLabels]);

  useEffect(() => {
    if (appointments.length > 0) {
      // Extract unique tutors
      const tutors = [...new Set(appointments.map(appt => getTutorName(appt)))].sort();
      setAvailableTutors(tutors);

      // Extract unique statuses
      const statuses = [...new Set(appointments.map(appt => appt.status))].sort();
      setAvailableStatuses(statuses);
    }
  }, [appointments]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, []);

  // Pagination now handled server-side; use the page set returned from the backend directly
  const paginatedAppointments = filteredAppointments;

  // Use summary totals from backend (for all appointments in date range)
  const totalRevenue = useMemo(() => summaryTotals.totalRevenue, [summaryTotals.totalRevenue]);
  const totalTutorPay = useMemo(() => summaryTotals.totalTutorPay, [summaryTotals.totalTutorPay]);
  const totalProfit = useMemo(() => summaryTotals.totalProfit, [summaryTotals.totalProfit]);

  const totalAdHocPay = useMemo(() => 
    adHocCharges.reduce((sum, charge) => sum + (charge.pay_contractor || 0), 0),
    [adHocCharges]
  );

  // Memoize sorted data
  const sortedAdHocCharges = useMemo(() => 
    adHocCharges.slice().sort((a, b) => new Date(a.date_occurred) - new Date(b.date_occurred)),
    [adHocCharges]
  );

  const sortedAppointments = useMemo(() => 
    paginatedAppointments.slice().sort((a, b) => new Date(a.date) - new Date(b.date)),
    [paginatedAppointments]
  );

  // Memoize handlers
  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  const fetchAppointments = useCallback(async (page = 1, filters = {}) => {
    if (!startDate || !endDate) return;
    
    const fetchParams = {
      startDate,
      endDate,
      page,
      itemsPerPage,
      ...filters
    };
    
    // Check cache first
    const cacheKey = JSON.stringify(fetchParams);
    if (dataCache.current.has(cacheKey)) {
      const cachedData = dataCache.current.get(cacheKey);
      setAppointments(cachedData.appointments);
      setFilteredAppointments(cachedData.appointments);
      setAdHocCharges(cachedData.adHocCharges);
      setTotalItems(cachedData.totalItems);
      setTotalPages(cachedData.totalPages);
      setHasMoreData(cachedData.hasMoreData);
      if (cachedData.summaryTotals) {
        setSummaryTotals(cachedData.summaryTotals);
      }
      return;
    }
    
    setFetchLoading(true);
    setTableLoading(true);
    
    try {
      const startUTC = new Date(`${startDate}T00:00:00-05:00`).toISOString();
      const endUTC = new Date(`${endDate}T23:59:59-05:00`).toISOString();
      
      // Build query parameters for server-side filtering and pagination
      const queryParams = new URLSearchParams({
        start: startUTC,
        end: endUTC,
        page: page.toString(),
        limit: itemsPerPage.toString(),
        // Add server-side filters
        tutor: filters.tutor || '',
        status: filters.statuses ? filters.statuses.join(',') : '',
        labels: filters.labels ? filters.labels.join(',') : '',
        employmentType: filters.employmentTypes ? filters.employmentTypes.join(',') : '',
        search: filters.search || ''
      });
      
      logger.debug('Fetching appointments with server-side pagination:', { 
        startUTC, 
        endUTC, 
        page, 
        itemsPerPage,
        filters 
      });
      
      const response = await fetch(`/api/appointments?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error && errorData.error.includes('Date range too large')) {
          throw new Error(`Date range too large (${errorData.requestedDays} days). Please select a range of 365 days or less.`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const appointments = data.appointments || data;
      
      // Handle pagination metadata
      const totalItems = data.pagination?.total || appointments.length;
      const totalPages = data.pagination?.totalPages || Math.ceil(totalItems / itemsPerPage);
      const hasMoreData = data.pagination?.hasMore || false;
      
      logger.debug(`Fetched ${appointments.length} appointments (page ${page}/${totalPages})`, 
        `Total: ${totalItems}, Has more: ${hasMoreData}`);
      
      // Normalize ad hoc charges
      const normalizedCharges = Array.isArray(data.adHocCharges)
        ? data.adHocCharges.map(charge => ({
            ...charge,
            pay_contractor: parseFloat(charge.pay_contractor || 0),
            tutorName: charge.raw && charge.raw.contractor
              ? `${charge.raw.contractor.first_name || ''} ${charge.raw.contractor.last_name || ''}`.trim()
              : 'Unknown'
          }))
        : [];
      
      // Update state
      setAppointments(appointments);
      setFilteredAppointments(appointments);
      setAdHocCharges(normalizedCharges);
      setTotalItems(totalItems);
      setTotalPages(totalPages);
      setHasMoreData(hasMoreData);
      
      // Update summary totals from backend (for all appointments in date range)
      if (data.summaryTotals) {
        logger.debug('Received summary totals from backend:', data.summaryTotals);
        // Ensure all values are numbers and not NaN
          const sanitizedTotals = {
          totalRevenue: Number(data.summaryTotals.totalRevenue) || 0,
          totalTutorPay: Number(data.summaryTotals.totalTutorPay) || 0,
          totalProfit: Number(data.summaryTotals.totalProfit) || 0,
          totalAppointments: Number(data.summaryTotals.totalAppointments) || 0,
          completedRevenue: Number(data.summaryTotals.completedRevenue) || 0,
          completedTutorPay: Number(data.summaryTotals.completedTutorPay) || 0,
          completedProfit: Number(data.summaryTotals.completedProfit) || 0,
          completedAppointments: Number(data.summaryTotals.completedAppointments) || 0,
            completedDurationMinutes: Number(data.summaryTotals.completedDurationMinutes) || 0,
          statusCounts: data.summaryTotals.statusCounts || {}
        };
        setSummaryTotals(sanitizedTotals);
      } else {
        logger.debug('No summary totals received from backend, using defaults');
        // Fallback to calculating from the appointments we have
          const fallbackTotals = {
          totalRevenue: Number(appointments.reduce((sum, appt) => sum + (appt.revenue || 0), 0)) || 0,
          totalTutorPay: Number(appointments.reduce((sum, appt) => sum + (appt.tutorPay || 0), 0)) || 0,
          totalProfit: Number(appointments.reduce((sum, appt) => sum + ((appt.revenue || 0) - (appt.tutorPay || 0)), 0)) || 0,
          totalAppointments: Number(appointments.length) || 0,
          completedRevenue: Number(appointments.filter(a => a.status === 'complete' || a.status === 'cancelled-chargeable').reduce((sum, appt) => sum + (appt.revenue || 0), 0)) || 0,
          completedTutorPay: Number(appointments.filter(a => a.status === 'complete' || a.status === 'cancelled-chargeable').reduce((sum, appt) => sum + (appt.tutorPay || 0), 0)) || 0,
          completedProfit: Number(appointments.filter(a => a.status === 'complete' || a.status === 'cancelled-chargeable').reduce((sum, appt) => sum + ((appt.revenue || 0) - (appt.tutorPay || 0)), 0)) || 0,
          completedAppointments: Number(appointments.filter(a => a.status === 'complete' || a.status === 'cancelled-chargeable').length) || 0,
            completedDurationMinutes: (() => {
              const completedLessons = appointments.filter(a => a.status === 'complete');
              let minutes = 0;
              completedLessons.forEach(appt => {
                if (appt.start && appt.finish) {
                  minutes += Math.round((new Date(appt.finish) - new Date(appt.start)) / 60000);
                } else if (appt.units) {
                  minutes += Math.round(Number(appt.units) * 60);
                }
              });
              return minutes;
            })(),
          statusCounts: appointments.reduce((counts, appt) => {
            const status = appt.status || 'unknown';
            counts[status] = (counts[status] || 0) + 1;
            return counts;
          }, {})
        };
        setSummaryTotals(fallbackTotals);
      }
      
      // Cache the results
      dataCache.current.set(cacheKey, {
        appointments,
        adHocCharges: normalizedCharges,
        totalItems,
        totalPages,
        hasMoreData,
        summaryTotals: data.summaryTotals || summaryTotals
      });
      
      // Limit cache size to prevent memory issues
      if (dataCache.current.size > 50) {
        const firstKey = dataCache.current.keys().next().value;
        dataCache.current.delete(firstKey);
      }
      
      lastFetchParams.current = fetchParams;
      
    } catch (error) {
      console.error('Error fetching appointments:', error);
      alert(`Error fetching appointments: ${error.message}. Please try a smaller date range or contact support.`);
    } finally {
      setFetchLoading(false);
      setTableLoading(false);
    }
  }, [startDate, endDate, itemsPerPage]);

  // Apply filters when they change (but only if we have data loaded)
  useEffect(() => {
    // Clear existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Only apply filters if we have appointments loaded and dates are set
    if (appointments.length > 0 && startDate && endDate) {
      filterTimeoutRef.current = setTimeout(() => {
        const filterParams = {
          tutor: selectedTutor || '',
          statuses: selectedStatuses || [],
          labels: selectedLabels || [],
          employmentTypes: selectedEmploymentTypes || [],
          search: filterLabel || ''
        };
        
        // Reset to first page when filters change
        setCurrentPage(1);
        
        // Now fetchAppointments is defined, so we can call it
        fetchAppointments(1, filterParams);
      }, 800); // Increased debounce to 800ms for better performance
    }
  }, [selectedTutor, selectedStatuses, selectedLabels, selectedEmploymentTypes, filterLabel, fetchAppointments, appointments.length, startDate, endDate]);

  // Remove automatic fetch when dates change - user must manually click "Fetch" button

  const getTutorName = (appt) => {
    if (appt.cjas && appt.cjas.length > 0) {
      const tutorNames = appt.cjas.map(cja => cja.name).filter(name => name);
      if (tutorNames.length > 0) {
        return tutorNames.join(', ');
      }
    }
    if (appt.service && appt.service.conjobs && appt.service.conjobs.length > 0) {
      const tutorNames = appt.service.conjobs.map(conjob => conjob.name).filter(name => name);
      if (tutorNames.length > 0) {
        return tutorNames.join(', ');
      }
    }
    return 'Unknown';
  };

  const downloadCSV = async () => {
    if (!startDate || !endDate) {
      alert('Please select a date range first');
      return;
    }

    // Show loading state
    const originalText = downloadButtonRef.current?.textContent;
    if (downloadButtonRef.current) {
      downloadButtonRef.current.textContent = 'Downloading...';
      downloadButtonRef.current.disabled = true;
    }

    try {
      const startUTC = new Date(`${startDate}T00:00:00-05:00`).toISOString();
      const endUTC = new Date(`${endDate}T23:59:59-05:00`).toISOString();
      
      // Build query parameters for CSV download (no pagination, all appointments)
      const queryParams = new URLSearchParams({
        start: startUTC,
        end: endUTC,
        csv: 'true', // Special flag for CSV download
        // Add filters if any are applied
        tutor: selectedTutor || '',
        status: selectedStatuses.join(',') || '',
        labels: selectedLabels.join(',') || '',
        employmentType: selectedEmploymentTypes.join(',') || '',
        search: filterLabel || ''
      });
      
      logger.debug('Downloading CSV for all appointments in date range:', { startUTC, endUTC });
      logger.debug('CSV URL:', `/api/appointments/csv?${queryParams}`);
      
      const response = await fetch(`/api/appointments/csv?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      logger.debug('CSV response status:', response.status);
      logger.debug('CSV response headers:', response.headers);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `appointments_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert(`Error downloading CSV: ${error.message}`);
    } finally {
      // Restore button state
      if (downloadButtonRef.current) {
        downloadButtonRef.current.textContent = originalText;
        downloadButtonRef.current.disabled = false;
      }
    }
  };

  // Virtualized table row component
  const VirtualizedRow = useCallback(({ index, style }) => {
    const appt = sortedAppointments[index];
    if (!appt) return null;
    
    return (
      <div style={style} className="virtualized-row">
        <div style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '10px 0' }}>
          <div style={{ flex: '0 0 100px', padding: '0 10px' }}>{appt.appointmentId || 'N/A'}</div>
          <div style={{ flex: '0 0 100px', padding: '0 10px' }}>{new Date(appt.date).toLocaleDateString()}</div>
          <div style={{ flex: '0 0 80px', padding: '0 10px' }}>
            {(() => {
              // Always prefer start/finish calculation when available as it's more accurate
              if (appt.start && appt.finish) {
                const durationMs = new Date(appt.finish) - new Date(appt.start);
                const hours = Math.floor(durationMs / (1000 * 60 * 60));
                const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                return `${hours}h ${minutes}m`;
              }
              // Fall back to units field (1 unit = 1 hour) if start/finish not available
              if (appt.units) {
                const hours = Math.floor(appt.units);
                const minutes = Math.round((appt.units - hours) * 60);
                return `${hours}h ${minutes}m`;
              }
              return 'N/A';
            })()}
          </div>
          <div style={{ flex: '1', padding: '0 10px' }}>
            {appt.lessonType ? (
              <a
                href={`https://account.storytimechess.com/cal/appointments/${appt.appointmentId}/`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#6d4aff', textDecoration: 'none', fontWeight: 'bold' }}
              >
                {appt.lessonType}
              </a>
            ) : 'N/A'}
          </div>
          <div style={{ flex: '0 0 150px', padding: '0 10px' }}>
            <div>
              {getTutorName(appt)}
              {(() => {
                const tutorNames = getTutorName(appt).split(', ').map(name => name.trim());
                const employmentTypes = tutorNames.map(tutorName => {
                  const contractor = allContractors.find(c => c.name === tutorName);
                  return contractor ? contractor.employment_type : null;
                }).filter(type => type);
                
                if (employmentTypes.length > 0) {
                  return (
                    <div style={{ marginTop: '4px' }}>
                      {employmentTypes.map((type, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            marginRight: '4px',
                            backgroundColor: type === 'w2' ? '#e8f5e8' : 
                                           type === '1099' ? '#fff3cd' : '#f8f9fa',
                            color: type === 'w2' ? '#27ae60' : 
                                  type === '1099' ? '#856404' : '#6c757d',
                            fontWeight: 'bold'
                          }}
                        >
                          {type === 'w2' ? 'W2' : type === '1099' ? '1099' : 'Unknown'}
                        </span>
                      ))}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
          <div style={{ flex: '0 0 100px', padding: '0 10px' }}>${appt.revenue.toFixed(2)}</div>
          <div style={{ flex: '0 0 100px', padding: '0 10px' }}>${appt.status === 'cancelled' ? '0.00' : (appt.tutorPay?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00')}</div>
          <div style={{ flex: '0 0 100px', padding: '0 10px' }}>{appt.status}</div>
          <div style={{ flex: '0 0 150px', padding: '0 10px' }}>
            {appt.service && Array.isArray(appt.service.labels) && appt.service.labels.length > 0
              ? (() => {
                  const filteredLabels = appt.service.labels
                    .filter(label => label.name !== 'First Lesson Complete');
                  return filteredLabels.length > 0
                    ? filteredLabels.map(label => label.name).join(', ')
                    : <span style={{ color: '#aaa' }}>None</span>;
                })()
              : <span style={{ color: '#aaa' }}>None</span>
            }
          </div>
        </div>
      </div>
    );
  }, [sortedAppointments]);

  return (
    <div className="h-full p-6">
      <style>
        {`
          .virtualized-row {
            background: #fff;
            transition: background-color 0.2s ease;
          }
          .virtualized-row:hover {
            background: #f8f9fa;
          }
          .virtualized-row:nth-child(even) {
            background: #fafbfc;
          }
          .virtualized-row:nth-child(even):hover {
            background: #f1f3f4;
          }
        `}
      </style>
      
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lessons Dashboard</h1>
        <p className="text-gray-600">View and manage your lessons and appointments</p>
      </div>

      <div className="max-w-6xl mx-auto space-y-6 lg:space-y-0 lg:flex lg:gap-6">
        {/* Fetch Appointments Section - Left Side */}
        <div className="lg:flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 style={{ marginBottom: '20px', color: '#333' }}>Fetch Lessons</h2>
          
          {/* Warning for large date ranges */}
          {startDate && endDate && (() => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
            if (daysDiff > 180) {
              return (
                <div style={{
                  background: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#856404'
                }}>
                  <strong>⚠️ Large Date Range Warning:</strong> You've selected {daysDiff} days. 
                  Large date ranges may take longer to load and could impact performance. 
                  Consider using smaller ranges (30-90 days) for better experience.
                </div>
              );
            }
            return null;
          })()}
          

          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              style={{ flex: '1', minWidth: '120px', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }} />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              style={{ flex: '1', minWidth: '120px', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }} />
            <button onClick={() => fetchAppointments(1)} style={{
              background: '#6A469D',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontWeight: 'bold',
              cursor: fetchLoading ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              opacity: fetchLoading ? 0.7 : 1
            }}>
              {fetchLoading ? 'Loading...' : 'Fetch Lessons'}
            </button>
          </div>

          {/* Month Selector */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>Quick Month Selector</h3>
            
            {/* Year Selector */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#4b5563', fontSize: '14px' }}>Year</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[2024, 2025, 2026].map((year) => {
                  const currentYear = new Date().getFullYear();
                  const isSelected = (() => {
                    if (!startDate || !endDate) return year === currentYear;
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    return start.getFullYear() === year && end.getFullYear() === year;
                  })();
                  
                  return (
                    <button
                      key={year}
                      onClick={() => {
                        // Keep the same month but change the year
                        const currentMonth = startDate ? new Date(startDate).getMonth() : new Date().getMonth();
                        const firstDay = new Date(year, currentMonth, 1);
                        const lastDay = new Date(year, currentMonth + 1, 0);
                        
                        setStartDate(firstDay.toISOString().split('T')[0]);
                        setEndDate(lastDay.toISOString().split('T')[0]);
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        backgroundColor: isSelected ? '#6A469D' : '#f3f4f6',
                        color: isSelected ? '#fff' : '#4b5563',
                        border: '1px solid',
                        borderColor: isSelected ? '#6A469D' : '#e5e7eb',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: year === currentYear ? 'bold' : 'normal',
                        transition: 'all 0.2s ease',
                        minWidth: '60px'
                      }}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {MONTHS.map((month, index) => {
                const currentDate = new Date();
                const isCurrentMonth = currentDate.getMonth() === month.value && currentDate.getFullYear() === new Date().getFullYear();
                
                // Debug logging (only in development)
                logger.debug(`Month ${month.name} (index ${index}):`, {
                  startDate,
                  endDate,
                  monthValue: month.value,
                  currentMonth: currentDate.getMonth(),
                  currentYear: currentDate.getFullYear()
                });
                
                const isSelected = (() => {
                  if (!startDate || !endDate) return isCurrentMonth;
                  
                  const start = new Date(startDate + 'T00:00:00');
                  const end = new Date(endDate + 'T00:00:00');
                  
                  logger.debug(`Month ${month.name} selection check:`, {
                    startDate,
                    endDate,
                    startParsed: start.toISOString(),
                    endParsed: end.toISOString(),
                    startMonth: start.getMonth(),
                    endMonth: end.getMonth(),
                    monthValue: month.value,
                    startYear: start.getFullYear(),
                    endYear: end.getFullYear()
                  });
                  
                  // Simple check: if start and end are in the same month as the button
                  const result = start.getMonth() === month.value && 
                         end.getMonth() === month.value && 
                         start.getFullYear() === end.getFullYear();
                         
                  logger.debug(`Month ${month.name} result:`, result);
                  
                  return result;
                })();
                
                return (
                  <button
                    key={month.value}
                    onClick={() => {
                      // Get the year from the currently selected year button
                      const currentYear = new Date().getFullYear();
                      let selectedYear = currentYear;
                      
                      if (startDate && endDate) {
                        const start = new Date(startDate + 'T00:00:00');
                        const end = new Date(endDate + 'T00:00:00');
                        // Use the year from the date range, but validate it's reasonable
                        const yearFromDates = start.getFullYear();
                        if (yearFromDates >= 2020 && yearFromDates <= 2030) {
                          selectedYear = yearFromDates;
                        }
                      }
                      
                      logger.debug(`Month click debug:`, {
                        startDate,
                        endDate,
                        currentYear,
                        selectedYear,
                        monthValue: month.value
                      });
                      
                      const firstDay = new Date(selectedYear, month.value, 1);
                      const lastDay = new Date(selectedYear, month.value + 1, 0);
                      
                      logger.debug(`Clicked month ${month.name} (value: ${month.value}):`, {
                        selectedYear,
                        firstDay: firstDay.toISOString().split('T')[0],
                        lastDay: lastDay.toISOString().split('T')[0]
                      });
                      
                      setStartDate(firstDay.toISOString().split('T')[0]);
                      setEndDate(lastDay.toISOString().split('T')[0]);
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '20px',
                      backgroundColor: isSelected ? '#6A469D' : '#f3f4f6',
                      color: isSelected ? '#fff' : '#4b5563',
                      border: '1px solid',
                      borderColor: isSelected ? '#6A469D' : '#e5e7eb',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isCurrentMonth ? 'bold' : 'normal',
                      transition: 'all 0.2s ease',
                      minWidth: '50px'
                    }}
                  >
                    {month.name}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  const currentDate = new Date();
                  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                  
                  setStartDate(firstDay.toISOString().split('T')[0]);
                  setEndDate(lastDay.toISOString().split('T')[0]);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '15px',
                  backgroundColor: '#34B256',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Current Month
              </button>
              <button
                onClick={() => {
                  const currentDate = new Date();
                  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
                  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
                  
                  setStartDate(firstDay.toISOString().split('T')[0]);
                  setEndDate(lastDay.toISOString().split('T')[0]);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '15px',
                  backgroundColor: '#50C8DF',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Last Month
              </button>
              <button
                onClick={() => {
                  const currentDate = new Date();
                  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
                  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
                  
                  setStartDate(firstDay.toISOString().split('T')[0]);
                  setEndDate(lastDay.toISOString().split('T')[0]);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '15px',
                  backgroundColor: '#F79A30',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Last 3 Months
              </button>
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '15px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Clear Dates
              </button>
            </div>
          </div>

          {/* Filter Controls */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>Filters</h3>
            
            {/* Search Filter */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#4b5563' }}>Search</label>
              <input
                type="text"
                value={filterLabel}
                onChange={(e) => setFilterLabel(e.target.value)}
                placeholder="Search by lesson type, status, or tutor..."
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  background: '#fff'
                }}
              />
            </div>

            {/* Tutor Filter removed (we use the search bar for tutor names) */}

            {/* Employment Type Filter */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#4b5563' }}>Employment Type</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {['w2', '1099', 'unknown'].map(employmentType => {
                  // Define colors for each employment type
                  const getEmploymentTypeColor = (type) => {
                    switch (type) {
                      case 'w2':
                        return '#27ae60'; // green
                      case '1099':
                        return '#f39c12'; // orange
                      case 'unknown':
                        return '#95a5a6'; // gray
                      default:
                        return '#6d4aff'; // default purple
                    }
                  };

                  const employmentTypeColor = getEmploymentTypeColor(employmentType);
                  const displayName = employmentType === 'w2' ? 'W2 Employees' : 
                                    employmentType === '1099' ? '1099 Contractors' : 
                                    'Unclassified';
                  
                  return (
                    <div
                      key={employmentType}
                      onClick={() => {
                        setSelectedEmploymentTypes(prev => 
                          prev.includes(employmentType)
                            ? prev.filter(t => t !== employmentType)
                            : [...prev, employmentType]
                        );
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        backgroundColor: selectedEmploymentTypes.includes(employmentType) ? employmentTypeColor : '#f3f4f6',
                        color: selectedEmploymentTypes.includes(employmentType) ? '#fff' : '#4b5563',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        border: '1px solid',
                        borderColor: selectedEmploymentTypes.includes(employmentType) ? employmentTypeColor : '#e5e7eb'
                      }}
                    >
                      {displayName}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Status Filter */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: '#4b5563' }}>Status</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {availableStatuses.map(status => {
                  // Define colors for each status
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'cancelled':
                        return '#ef4444'; // red
                      case 'cancelled-chargeable':
                      case 'complete':
                        return '#22c55e'; // green
                      case 'planned':
                        return '#eab308'; // yellow
                      default:
                        return '#6d4aff'; // default purple
                    }
                  };

                  const statusColor = getStatusColor(status);
                  
                  return (
                    <div
                      key={status}
                      onClick={() => {
                        setSelectedStatuses(prev => 
                          prev.includes(status)
                            ? prev.filter(s => s !== status)
                            : [...prev, status]
                        );
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '20px',
                        backgroundColor: selectedStatuses.includes(status) ? statusColor : '#f3f4f6',
                        color: selectedStatuses.includes(status) ? '#fff' : '#4b5563',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        border: '1px solid',
                        borderColor: selectedStatuses.includes(status) ? statusColor : '#e5e7eb'
                      }}
                    >
                      {status}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Labels Filter */}
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ color: '#4b5563' }}>Labels (OR logic - select multiple)</label>
                {selectedLabels.length > 0 && (
                  <button
                    onClick={() => setSelectedLabels([])}
                    style={{
                      background: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {availableLabels.map(label => (
                  <div
                    key={label}
                    onClick={() => {
                      setSelectedLabels(prev => 
                        prev.includes(label)
                          ? prev.filter(l => l !== label)
                          : [...prev, label]
                      );
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      backgroundColor: selectedLabels.includes(label) ? '#6d4aff' : '#f3f4f6',
                      color: selectedLabels.includes(label) ? '#fff' : '#4b5563',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      border: '1px solid',
                      borderColor: selectedLabels.includes(label) ? '#6d4aff' : '#e5e7eb'
                    }}
                  >
                    {label}
                  </div>
                ))}
              </div>
              {selectedLabels.length > 0 && (
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  Showing appointments with any of: {selectedLabels.join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="lg:flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 style={{ marginBottom: '20px', color: '#333' }}>Lessons Summary</h2>
          <div style={{ marginTop: '10px' }}>
            <p>Number of Lessons: <strong>{summaryTotals.totalAppointments || 0}</strong></p>
            <p>Completed Lessons: <strong style={{ color: '#34B256' }}>{summaryTotals.completedAppointments || 0}</strong></p>
            <p>Cancelled Lessons: <strong style={{ color: '#f44336' }}>{(summaryTotals.statusCounts?.cancelled || 0)}</strong></p>
            <p>Pending Lessons: <strong style={{ color: '#ffc107' }}>{(summaryTotals.statusCounts?.planned || 0)}</strong></p>
            <p>Total Duration (Completed): <strong style={{ color: '#2196f3' }}>{(() => {
              const totalMinutes = summaryTotals.completedDurationMinutes || 0;
              const hours = Math.floor(totalMinutes / 60);
              const minutes = totalMinutes % 60;
              return `${hours}h ${minutes}m`;
            })()}</strong></p>
          </div>

          <div style={{ marginTop: '30px' }}>
            <h2 style={{ marginBottom: '20px', color: '#333' }}>Financial Summary</h2>
            <p>Total Revenue Collected: <strong>${(summaryTotals.completedRevenue || 0).toLocaleString()}</strong></p>
            <p>Total Tutor Pay: <strong>${(summaryTotals.completedTutorPay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></p>
            <p>Total Profit: <strong>${(summaryTotals.completedProfit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></p>
            <p>Profit Margin: <strong>{(summaryTotals.completedRevenue || 0) === 0 ? '0.00' : (((summaryTotals.completedProfit || 0) / (summaryTotals.completedRevenue || 1)) * 100).toFixed(2)}%</strong></p>
          </div>

          <div style={{ marginTop: '30px' }}>
            <h2 style={{ marginBottom: '20px', color: '#333' }}>Ad Hoc Pay Summary</h2>
            <p>Total Ad Hoc Tutor Pay: <strong>${adHocCharges
              .reduce((sum, charge) => sum + (charge.pay_contractor || 0), 0)
              .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></p>
          </div>

          <div style={{ marginTop: '30px' }}>
            <h2 style={{ marginBottom: '20px', color: '#333' }}>Performance Metrics</h2>
            <p>Average Revenue per Lesson: <strong>${(summaryTotals.completedAppointments || 0) === 0 ? '0.00' : ((summaryTotals.completedRevenue || 0) / (summaryTotals.completedAppointments || 1)).toFixed(2)}</strong></p>
            <p>Average Tutor Pay per Lesson: <strong>${(summaryTotals.completedAppointments || 0) === 0 ? '0.00' : ((summaryTotals.completedTutorPay || 0) / (summaryTotals.completedAppointments || 1)).toFixed(2)}</strong></p>
            <p>Average Profit per Lesson: <strong>${(summaryTotals.completedAppointments || 0) === 0 ? '0.00' : ((summaryTotals.completedProfit || 0) / (summaryTotals.completedAppointments || 1)).toFixed(2)}</strong></p>
          </div>
        </div>
      </div>

      {/* Appointments Table - Centered Below */}
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div style={{ position: 'relative' }}>
          <h2 style={{ marginBottom: '20px', color: '#333' }}>Appointments</h2>
          <div style={{ position: 'absolute', top: '0', right: '0', display: 'flex', gap: '10px' }}>
            {/* Virtualization Toggle */}
            {filteredAppointments.length > 500 && (
              <button
                onClick={() => setUseVirtualization(!useVirtualization)}
                style={{
                  background: useVirtualization ? '#dc3545' : '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title={useVirtualization ? 'Switch to regular table' : 'Switch to virtualized table for better performance'}
              >
                {useVirtualization ? 'Regular Table' : 'Virtual Table'}
              </button>
            )}
            <button 
              ref={downloadButtonRef}
              onClick={downloadCSV} 
              disabled={!startDate || !endDate} 
              style={{
                background: (!startDate || !endDate) ? '#a5d6a7' : '#34B256',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontWeight: 'bold',
                cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer'
              }}
            >
              Download CSV
            </button>
          </div>
        </div>

        {/* Pagination Info */}
        <div style={{ 
          marginBottom: '20px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '10px 0',
          borderBottom: '1px solid #eee'
        }}>
          <div style={{ color: '#666', fontSize: '14px' }}>
            Showing {filteredAppointments.length} appointments (Page {currentPage} of {totalPages})

          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => {
                  setCurrentPage(1);
                  fetchAppointments(1, {
                    tutor: selectedTutor,
                    statuses: selectedStatuses,
                    labels: selectedLabels,
                    employmentTypes: selectedEmploymentTypes,
                    search: filterLabel
                  });
                }}
                disabled={currentPage === 1 || tableLoading}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  background: currentPage === 1 || tableLoading ? '#f5f5f5' : '#fff',
                  color: currentPage === 1 || tableLoading ? '#999' : '#333',
                  cursor: currentPage === 1 || tableLoading ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                First
              </button>
              <button
                onClick={() => {
                  const newPage = Math.max(1, currentPage - 1);
                  setCurrentPage(newPage);
                  fetchAppointments(newPage, {
                    tutor: selectedTutor,
                    statuses: selectedStatuses,
                    labels: selectedLabels,
                    employmentTypes: selectedEmploymentTypes,
                    search: filterLabel
                  });
                }}
                disabled={currentPage === 1 || tableLoading}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  background: currentPage === 1 || tableLoading ? '#f5f5f5' : '#fff',
                  color: currentPage === 1 || tableLoading ? '#999' : '#333',
                  cursor: currentPage === 1 || tableLoading ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                Previous
              </button>
              
              <span style={{ fontSize: '14px', color: '#666' }}>
                Page {currentPage} of {totalPages} {tableLoading && '(Loading...)'}
              </span>
              
              <button
                onClick={() => {
                  const newPage = Math.min(totalPages, currentPage + 1);
                  setCurrentPage(newPage);
                  fetchAppointments(newPage, {
                    tutor: selectedTutor,
                    statuses: selectedStatuses,
                    labels: selectedLabels,
                    employmentTypes: selectedEmploymentTypes,
                    search: filterLabel
                  });
                }}
                disabled={currentPage === totalPages || tableLoading}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  background: currentPage === totalPages || tableLoading ? '#f5f5f5' : '#fff',
                  color: currentPage === totalPages || tableLoading ? '#999' : '#333',
                  cursor: currentPage === totalPages || tableLoading ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                Next
              </button>
              <button
                onClick={() => {
                  setCurrentPage(totalPages);
                  fetchAppointments(totalPages, {
                    tutor: selectedTutor,
                    statuses: selectedStatuses,
                    labels: selectedLabels,
                    employmentTypes: selectedEmploymentTypes,
                    search: filterLabel
                  });
                }}
                disabled={currentPage === totalPages || tableLoading}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  background: currentPage === totalPages || tableLoading ? '#f5f5f5' : '#fff',
                  color: currentPage === totalPages || tableLoading ? '#999' : '#333',
                  cursor: currentPage === totalPages || tableLoading ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                Last
              </button>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          {tableLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              fontSize: '16px'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '4px solid #f3f3f3', 
                  borderTop: '4px solid #6A469D', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }}></div>
              </div>
              Loading appointments... {totalItems > 1000 ? `Processing ${totalItems} total records...` : 'Please wait...'}
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : useVirtualization && sortedAppointments.length > 500 ? (
            // Virtualized table for large datasets
            <div style={{ height: '600px' }}>
              {/* Virtualized table header */}
              <div style={{ 
                display: 'flex', 
                background: '#f0f0f5', 
                padding: '10px 0',
                borderBottom: '2px solid #ddd',
                fontWeight: 'bold'
              }}>
                <div style={{ flex: '0 0 100px', padding: '0 10px' }}>ID</div>
                <div style={{ flex: '0 0 100px', padding: '0 10px' }}>Date</div>
                <div style={{ flex: '0 0 80px', padding: '0 10px' }}>Duration</div>
                <div style={{ flex: '1', padding: '0 10px' }}>Lesson Type</div>
                <div style={{ flex: '0 0 150px', padding: '0 10px' }}>Tutor</div>
                <div style={{ flex: '0 0 100px', padding: '0 10px' }}>Revenue</div>
                <div style={{ flex: '0 0 100px', padding: '0 10px' }}>Tutor Pay</div>
                <div style={{ flex: '0 0 100px', padding: '0 10px' }}>Status</div>
                <div style={{ flex: '0 0 150px', padding: '0 10px' }}>Labels</div>
              </div>
              
              {/* Virtualized list */}
              <List
                height={540}
                itemCount={sortedAppointments.length}
                itemSize={50}
                width="100%"
              >
                {VirtualizedRow}
              </List>
            </div>
          ) : (
            // Regular table for smaller datasets
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f0f0f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Duration</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Lesson Type</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Tutor</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Revenue</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Tutor Pay</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Labels</th>
                </tr>
              </thead>
              <tbody>
                {sortedAppointments.length > 0 ? (
                  sortedAppointments.map((appt, index) => (
                    <tr key={`${appt.appointmentId}-${index}`}>
                      <td style={{ padding: '10px' }}>{appt.appointmentId || 'N/A'}</td>
                      <td style={{ padding: '10px' }}>{new Date(appt.date).toLocaleDateString()}</td>
                      <td style={{ padding: '10px' }}>
                        {(() => {
                          // Always prefer start/finish calculation when available as it's more accurate
                          if (appt.start && appt.finish) {
                            const durationMs = new Date(appt.finish) - new Date(appt.start);
                            const hours = Math.floor(durationMs / (1000 * 60 * 60));
                            const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
                            return `${hours}h ${minutes}m`;
                          }
                          // Fall back to units field (1 unit = 1 hour) if start/finish not available
                          if (appt.units) {
                            const hours = Math.floor(appt.units);
                            const minutes = Math.round((appt.units - hours) * 60);
                            return `${hours}h ${minutes}m`;
                          }
                          return 'N/A';
                        })()}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {appt.lessonType ? (
                          <a
                            href={`https://account.storytimechess.com/cal/appointments/${appt.appointmentId}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#6d4aff', textDecoration: 'none', fontWeight: 'bold' }}
                          >
                            {appt.lessonType}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <div>
                          {getTutorName(appt)}
                          {(() => {
                            const tutorNames = getTutorName(appt).split(', ').map(name => name.trim());
                            const employmentTypes = tutorNames.map(tutorName => {
                              const contractor = allContractors.find(c => c.name === tutorName);
                              return contractor ? contractor.employment_type : null;
                            }).filter(type => type);
                            
                            if (employmentTypes.length > 0) {
                              return (
                                <div style={{ marginTop: '4px' }}>
                                  {employmentTypes.map((type, index) => (
                                    <span
                                      key={index}
                                      style={{
                                        padding: '2px 6px',
                                        borderRadius: '10px',
                                        fontSize: '10px',
                                        marginRight: '4px',
                                        backgroundColor: type === 'w2' ? '#e8f5e8' : 
                                                       type === '1099' ? '#fff3cd' : '#f8f9fa',
                                        color: type === 'w2' ? '#27ae60' : 
                                              type === '1099' ? '#856404' : '#6c757d',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {type === 'w2' ? 'W2' : type === '1099' ? '1099' : 'Unknown'}
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </td>
                      <td style={{ padding: '10px' }}>${appt.revenue.toFixed(2)}</td>
                      <td style={{ padding: '10px' }}>${appt.status === 'cancelled' ? '0.00' : (appt.tutorPay?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00')}</td>
                      <td style={{ padding: '10px' }}>{appt.status}</td>
                      <td style={{ padding: '10px' }}>
                        {appt.service && Array.isArray(appt.service.labels) && appt.service.labels.length > 0
                          ? (() => {
                              const filteredLabels = appt.service.labels
                                .filter(label => label.name !== 'First Lesson Complete');
                              return filteredLabels.length > 0
                                ? filteredLabels.map(label => label.name).join(', ')
                                : <span style={{ color: '#aaa' }}>None</span>;
                            })()
                          : <span style={{ color: '#aaa' }}>None</span>
                        }
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ padding: '10px', textAlign: 'center', color: '#888' }}>
                      No appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Ad Hoc Charges Section */}
      <div id="ad-hoc-charges" style={{
        maxWidth: '1200px',
        margin: '20px auto',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        padding: '30px'
      }}>
        <h2 style={{ marginBottom: '20px', color: '#333' }}>Ad Hoc Charges</h2>
        {adHocCharges.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>ID</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Date</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Description</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Tutor</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Pay Contractor</th>
              </tr>
            </thead>
            <tbody>
              {sortedAdHocCharges.map((charge, index) => (
                <tr key={index}>
                  <td style={{ padding: '10px' }}>{charge.chargeId || 'N/A'}</td>
                  <td style={{ padding: '10px' }}>{new Date(charge.date_occurred).toLocaleDateString()}</td>
                  <td style={{ padding: '10px' }}>
                    <a 
                      href={`https://app.tutorcruncher.com/accounting/adhoccharges/${charge.chargeId}/`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#6d4aff', textDecoration: 'none', fontWeight: 'bold' }}
                    >
                      {charge.description}
                    </a>
                  </td>
                  <td style={{ padding: '10px' }}>{charge.tutorName || 'Unknown'}</td>
                  <td style={{ padding: '10px' }}>${charge.pay_contractor.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}